import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createSeedVarietyProduct, fetchSeedVarietiesAdmin, getToken, uploadFile } from './api';
import { AdminImagePreview, fileInput, input, label } from './adminUi';
import VarietySpecsFields from './VarietySpecsFields';

export default function SeedVarietyProductNew() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [productLineLabel, setProductLineLabel] = useState('');
  const [summary, setSummary] = useState('');
  const [urlId, setUrlId] = useState('');
  const [variantName, setVariantName] = useState('');
  const [variantBreeder, setVariantBreeder] = useState('');
  const [variantImage, setVariantImage] = useState('');
  const [variantAlt, setVariantAlt] = useState('');
  const [variantUrlId, setVariantUrlId] = useState('');
  const [specs, setSpecs] = useState([{ label: '', value: '' }]);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingCats, setLoadingCats] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    let cancelled = false;
    fetchSeedVarietiesAdmin()
      .then((block) => {
        if (cancelled) return;
        setCategories(Array.isArray(block?.categories) ? block.categories : []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCats(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function onUpload(file) {
    if (!file) return;
    try {
      const { url } = await uploadFile(file);
      setVariantImage(url);
    } catch (e) {
      setErr(e.message || 'Upload failed');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    if (!categoryId.trim()) {
      setErr('Select a category (create one under Categories if the list is empty).');
      return;
    }
    setSaving(true);
    try {
      const payload = { categoryId: categoryId.trim(), summary, productLabel: productLineLabel };
      if (urlId.trim()) payload.id = urlId.trim();
      const vName = variantName.trim();
      if (vName) {
        payload.variant = {
          name: vName,
          breeder: variantBreeder,
          image: variantImage,
          alt: variantAlt,
          specs: specs.filter((r) => r.label?.trim() || r.value?.trim()),
        };
        if (variantUrlId.trim()) payload.variant.id = variantUrlId.trim();
      }
      const { createdProductId } = await createSeedVarietyProduct(payload);
      if (typeof createdProductId !== 'string' || !createdProductId.trim()) {
        throw new Error('Server did not return product id');
      }
      navigate(`/admin/dashboard/seed-varieties/products/${encodeURIComponent(createdProductId)}/edit`);
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <Link
            to="/admin/dashboard/seed-varieties"
            className="text-xs font-medium text-brand-olive hover:underline"
          >
            ← Variety specs
          </Link>
          <h2 className="mt-1 text-lg font-bold text-neutral-900 md:text-xl">New product</h2>
        </div>
      </header>

      <main className="min-h-0 w-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 md:px-8 md:py-8">
        {err ? <p className="mb-4 text-sm text-red-600">{err}</p> : null}
        <form onSubmit={handleSubmit} className="w-full max-w-none rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-neutral-800">Product</h3>
          <p className="mt-1 text-xs text-neutral-500">
            Choose a{' '}
            <Link to="/admin/dashboard/seed-varieties/categories" className="font-semibold text-brand-olive hover:underline">
              category
            </Link>{' '}
            (create categories there first).
          </p>

          <label className={`${label} mt-4`}>
            Category
            <select
              className={input}
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
              disabled={loadingCats}
            >
              <option value="">{loadingCats ? 'Loading…' : 'Select category…'}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.id}
                </option>
              ))}
            </select>
          </label>
          {!loadingCats && categories.length === 0 ? (
            <p className="mt-3 text-sm text-amber-800">
              No categories yet.{' '}
              <Link to="/admin/dashboard/seed-varieties/categories/new" className="font-semibold underline">
                Create a category
              </Link>{' '}
              first.
            </p>
          ) : null}

          <label className={`${label} mt-4`}>
            Product line label (optional)
            <input
              className={input}
              value={productLineLabel}
              onChange={(e) => setProductLineLabel(e.target.value)}
              placeholder="Shown on the public index if set; otherwise the category name is used"
            />
          </label>
          <label className={`${label} mt-4`}>
            Summary
            <textarea
              className={`${input} mt-1.5 min-h-[5rem]`}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Short blurb on the index page"
            />
          </label>
          {/* <label className={`${label} mt-4`}>
            Product URL id (optional)
            <input
              className={input}
              value={urlId}
              onChange={(e) => setUrlId(e.target.value)}
              placeholder="Auto from product line / category if empty"
            />
            <span className="mt-1 block text-[11px] font-normal text-neutral-500">
              Public URL: /seed-varieties/<strong>this-id</strong>/variant-id
            </span>
          </label> */}

          <div className="mt-10 border-t border-neutral-200 pt-8">
            <h3 className="text-sm font-semibold text-neutral-800">First variant (optional)</h3>
            <p className="mt-1 text-xs text-neutral-500">
              Add one variety now, or leave blank and add variants on the next screen.
            </p>

            <AdminImagePreview src={variantImage} alt={variantAlt} emptyLabel="No image" />

            <div className="grid gap-4 sm:grid-cols-2">
              <label className={`${label} sm:col-span-2`}>
                Variety name
                <input
                  className={input}
                  value={variantName}
                  onChange={(e) => setVariantName(e.target.value)}
                  placeholder="e.g. Mintoo Super F1"
                />
              </label>
              <label className={`${label} sm:col-span-2`}>
                Breeder
                <input className={input} value={variantBreeder} onChange={(e) => setVariantBreeder(e.target.value)} />
              </label>
              <div className="sm:col-span-2 rounded-xl border border-neutral-200/90 bg-neutral-50/80 p-4">
                <p className="mb-2 text-xs font-semibold uppercase text-neutral-500">Image</p>
                <label className={`${label} mb-0`}>
                  Upload
                  <input type="file" accept="image/*" className={fileInput} onChange={(e) => onUpload(e.target.files?.[0])} />
                </label>
                <label className={`${label} mt-3`}>
                  Image URL
                  <input className={input} value={variantImage} onChange={(e) => setVariantImage(e.target.value)} />
                </label>
                <label className={`${label} mt-3`}>
                  Alt text
                  <input className={input} value={variantAlt} onChange={(e) => setVariantAlt(e.target.value)} />
                </label>
              </div>
              {/* <label className={`${label} sm:col-span-2`}>
                Variant URL id (optional)
                <input
                  className={input}
                  value={variantUrlId}
                  onChange={(e) => setVariantUrlId(e.target.value)}
                  placeholder="Auto from variety name if empty"
                />
              </label> */}
            </div>

            <div className="mt-6">
              <VarietySpecsFields specs={specs} onChange={setSpecs} />
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3 border-t border-neutral-200 pt-8">
            <button
              type="submit"
              disabled={saving || categories.length === 0}
              className="rounded-lg bg-brand-forest px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Create product'}
            </button>
            <Link
              to="/admin/dashboard/seed-varieties"
              className="rounded-lg border border-neutral-300 bg-white px-5 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </main>
    </>
  );
}
